import React, { useMemo, useState } from "react";
import type { LucideIcon } from "lucide-react";
import {
  MapPin,
  CircleGauge,
  Radio,
  BookOpen,
  Archive,
  ChefHat,
  User,
  Beer,
  ListTree,
  BarChart3,
  SlidersHorizontal,
  ScrollText,
  Percent,
  ArrowLeftRight,
  FileText,
  Users,
  CreditCard,
  IdCard,
  Receipt,
  ChevronDown,
  Banknote,
} from "lucide-react";
import { useLocation } from "wouter";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { usePosAuth } from "@/hooks/pos/use-pos-auth";
import { cn } from "@/lib/utils";
import { IS_TESTNET } from "@/constants/walletConnectionConfig";
import EthToSechDialog from "@/components/admin/EthToSechDialog";

interface SidebarItem {
  key: string;
  label: string;
  Icon: LucideIcon;
  path?: string;
  action?: string;
  aria: string;
  exact?: boolean;
  roles?: string[];
  testnetOnly?: boolean;
}

interface SidebarSection {
  heading: string;
  items: SidebarItem[];
  roles?: string[];
}

const SECTIONS: SidebarSection[] = [
  {
    heading: "Overview",
    items: [
      { key: "dashboard", label: "Dashboard", Icon: CircleGauge, path: "/admin", aria: "Go to Dashboard", exact: true },
    ],
  },
  {
    heading: "Orders",
    items: [
      { key: "orders", label: "Orders", Icon: Radio, path: "/admin/orders", aria: "Go to Orders", exact: true },
      { key: "live-orders", label: "Live Orders", Icon: ChefHat, path: "/admin/live-orders", aria: "Go to Live Orders" },
    ],
  },
  {
    heading: "Catalog",
    items: [
      { key: "products", label: "Products", Icon: Archive, path: "/admin/products", aria: "Go to Products" },
      { key: "menu-types", label: "Menus", Icon: BookOpen, path: "/admin/menu-types", aria: "Go to Menus" },
      { key: "subcategories", label: "Subcategories", Icon: ListTree, path: "/admin/subcategories", aria: "Go to Subcategories" },
      { key: "modifiers", label: "Modifiers", Icon: SlidersHorizontal, path: "/admin/modifier-groups", aria: "Go to Modifiers" },
      { key: "discounts", label: "Discounts", Icon: Percent, path: "/admin/discounts", aria: "Go to Discounts" },
      { key: "custom-drinks", label: "Custom Drinks", Icon: Beer, path: "/admin/custom-drinks", aria: "Go to Custom Drinks" },
    ],
  },
  {
    heading: "Settings",
    items: [
      { key: "locations", label: "Locations", Icon: MapPin, path: "/admin/locations", aria: "Go to Locations" },
      { key: "pos-users", label: "POS Users", Icon: User, path: "/admin/pos-users", aria: "Go to POS Users" },
    ],
  },
  {
    heading: "Platform",
    items: [
      { key: "transactions", label: "Transactions", Icon: ArrowLeftRight, path: "/admin/transactions", aria: "Go to Transactions" },
      { key: "waivers", label: "Waivers", Icon: FileText, path: "/admin/waivers", aria: "Go to Waivers" },
      { key: "platform-users", label: "Users", Icon: Users, path: "/admin/platform-users", aria: "Go to Users" },
      { key: "entry-passes", label: "Entry Passes", Icon: CreditCard, path: "/admin/entry-passes", aria: "Go to Entry Passes" },
      { key: "memberships", label: "Memberships", Icon: IdCard, path: "/admin/memberships", aria: "Go to Memberships" },
      { key: "tax-settings", label: "Tax Settings", Icon: Receipt, path: "/admin/tax-settings", aria: "Go to Tax Settings" },
      { key: "eth-to-sech", label: "ETH to SECH", Icon: Banknote, action: "eth-to-sech", aria: "Open ETH to SECH", testnetOnly: true },
    ],
  },
  {
    heading: "Analytics",
    items: [
      { key: "reports", label: "Reports", Icon: BarChart3, path: "/admin/reports", aria: "Go to Reports" },
      { key: "activity-logs", label: "Activity Logs", Icon: ScrollText, path: "/admin/activity-logs", aria: "Go to Activity Logs" },
    ],
  },
];

export default function Sidebar({
  width = "w-[248px]",
  isOpen = true,
}: {
  width?: string;
  isOpen?: boolean;
}) {
  const collapsed = width === "w-[54px]";
  const [location, setLocation] = useLocation();
  const { user } = usePosAuth();
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
  const [ethToSechOpen, setEthToSechOpen] = useState(false);

  const filteredSections = useMemo(() => {
    const userRole = user?.role;
    if (!userRole) return [];

    return SECTIONS
      .filter((section) => !section.roles || section.roles.includes(userRole))
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => {
          if (item.testnetOnly && !IS_TESTNET) return false;
          if (item.roles && !item.roles.includes(userRole)) return false;
          return true;
        }),
      }))
      .filter((section) => section.items.length > 0);
  }, [user?.role]);

  React.useEffect(() => {
    const prev = document.body.style.overflow;
    if (isOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = prev;
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const goTo = (path: string) => {
    setLocation(path);
  };

  const handleAction = (action: string) => {
    if (action === "eth-to-sech") {
      setEthToSechOpen(true);
    }
  };

  const isActive = (path: string | undefined, exact?: boolean) => {
    if (!path) return false;
    return exact ? location === path : location === path || location.startsWith(path + "/");
  };

  const toggleSection = (heading: string) => {
    setCollapsedSections((prev) => ({ ...prev, [heading]: !prev[heading] }));
  };

  return (
    <TooltipProvider delayDuration={100}>
      <div className="flex" aria-hidden={false}>
        <button aria-hidden={false} tabIndex={isOpen ? 0 : -1} />

        <aside
          className={`transform transition-all duration-300 ease-in-out h-full bg-[#FFFFFF] border-r border-gray-200 shadow-sm z-50 flex flex-col ${isOpen ? "translate-x-0" : "-translate-x-full"} ${width}`}
          role="dialog"
          aria-modal="true"
          aria-label="Admin sidebar"
        >
          <div className="flex-1 overflow-y-auto overflow-x-hidden px-2 py-2 transition-all duration-300 ease">
            <nav className="flex flex-col" aria-label="Sidebar navigation">
              {filteredSections.map((section, sectionIndex) => {
                const isSectionCollapsed = !!collapsedSections[section.heading];

                return (
                  <div key={section.heading} className={sectionIndex > 0 ? "mt-3 pt-3 border-t border-gray-200" : ""}>
                    {/* Section heading — clickable to toggle */}
                    <button
                      type="button"
                      onClick={() => !collapsed && toggleSection(section.heading)}
                      className={cn(
                        "flex items-center justify-between w-full text-[11px] font-semibold uppercase tracking-wider text-[#8993A4] mb-1 px-2 transition-opacity duration-300 ease-in-out hover:text-[#505258]",
                        collapsed ? "opacity-0 h-0 overflow-hidden pointer-events-none" : "opacity-100"
                      )}
                      aria-expanded={!isSectionCollapsed}
                    >
                      <span>{section.heading}</span>
                      <ChevronDown
                        className={cn(
                          "h-3 w-3 transition-transform duration-200",
                          isSectionCollapsed && "-rotate-90"
                        )}
                      />
                    </button>

                    {/* Items — hidden when section is collapsed (unless sidebar is collapsed) */}
                    <div
                      className={cn(
                        "flex flex-col gap-[1px] transition-all duration-200 overflow-hidden",
                        !collapsed && isSectionCollapsed ? "max-h-0 opacity-0" : "max-h-[500px] opacity-100"
                      )}
                    >
                      {section.items.map(({ key, label, Icon, path, action, aria, exact }) => {
                        const selected = isActive(path, exact);

                        const button = (
                          <button
                            onClick={() => action ? handleAction(action) : path && goTo(path)}
                            className={`flex items-center gap-2 w-full text-left rounded-[4px] transition-all duration-300 ease h-[38px] px-2 text-[#505258] ${selected ? "bg-[#F8E7E7]" : "hover:bg-[#E3E5F0] hover:text-[#172B4D]"}`}
                            aria-current={selected ? "page" : undefined}
                            aria-label={aria ?? label}
                          >
                            <span
                              className={`flex items-center justify-center rounded-[4px] text-sm flex-shrink-0 transition-all duration-150 ${selected ? "ring-1 ring-[#F8E7E7] text-[#172B4D]" : "border-transparent"}`}
                            >
                              <Icon className="h-[18px] w-[18px]" />
                            </span>

                            <span
                              className={`text-[13px] leading-[15px] font-medium whitespace-nowrap overflow-hidden transition-opacity duration-300 ease-in-out ${collapsed ? "opacity-0 pointer-events-none w-0" : "opacity-100 delay-150"} ${selected ? "text-[#172B4D]" : ""}`}
                            >
                              {label}
                            </span>
                          </button>
                        );

                        return collapsed ? (
                          <Tooltip key={key}>
                            <TooltipTrigger asChild>{button}</TooltipTrigger>
                            <TooltipContent
                              side="right"
                              sideOffset={8}
                              className="bg-[var(--primary-text-color)] text-white z-50"
                            >
                              {label}
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <React.Fragment key={key}>{button}</React.Fragment>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </nav>
          </div>
        </aside>
      </div>

      {/* ETH to SECH modal */}
      <EthToSechDialog open={ethToSechOpen} onOpenChange={setEthToSechOpen} />
    </TooltipProvider>
  );
}
